import {createEisTypeObject, getKey, getByKeyPath} from 'Quoting/utils/utilities';
import {Map as ImmutableMap} from 'immutable';

export const createNewGHI3PlanObjectWithDefaults = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  coverageEffectiveDate: string,
  defaultValues: ImmutableMap<string, any>
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    _type: 'HIBenefitPlan',
    exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
    planCd: plan.planCode,
    planName: plan.name,
    planGroupsLinks: [],
    offerStatus: 'offered',
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory')
      .map(() => createCoverageDef(defaultValues, rootId, parentId, coverageEffectiveDate)),
    rateAdjustmentLimits: {
      ...createEisTypeObject('HIRateAdjustmentLimits', rootId, parentId),
      maxAdjustmentPct: getByKeyPath(defaultValues, 'HIRateAdjustmentLimits.maxAdjustmentPct'),
      minAdjustmentPct: getByKeyPath(defaultValues, 'HIRateAdjustmentLimits.minAdjustmentPct'),
    },
  };
};

const createCoverageDef = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  planParentId: string,
  coverageEffectiveDate: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;

  return {
    _type: 'HICoverageDefinition',
    _key: key,
    benefitAmountChild: {
      benefitPct: defaultValues.get('HIBenefitAmountChild.benefitPct'),
      ...createEisTypeObject('HIBenefitAmountChild', rootId, parentId),
    },
    benefitAmountSpouse: {
      benefitPct: defaultValues.get('HIBenefitAmountSpouse.benefitPct'),
      ...createEisTypeObject('HIBenefitAmountSpouse', rootId, parentId),
    },
    benefitPayment: defaultValues.get('HICoverageDefinition.benefitPayment'),
    benefitSchedule: {
      isHealthScreeningApplied: defaultValues.get('HIBenefitSchedule.isHealthScreeningApplied'),
      ...createEisTypeObject('HIBenefitSchedule', rootId, parentId),
    },
    benefitStructure: createGHI3BenefitStructure(defaultValues, rootId, parentId),
    code: defaultValues.get('HICoverageDefinition.code'),
    coverageBasisCd: defaultValues.get('HICoverageDefinition.coverageBasisCd'),
    coverageGroupsLinks: [],
    coveredEvents: defaultValues.get('HICoverageDefinition.coveredEvents'),
    effDate: coverageEffectiveDate,
    eligibilities: createHIEligibilities(defaultValues, rootId, parentId),
    fundingStructure: {
      requiredGroupAccessPct: defaultValues.get('HIFundingStructure.requiredGroupAccessPct'),
      taxabilityCd: defaultValues.get('HIFundingStructure.taxabilityCd'),
      minParticipantsNum: defaultValues.get('HIFundingStructure.minParticipantsNum'),
      contributionTypeCd: defaultValues.get('HIFundingStructure.contributionTypeCd'),
      fundingTypeCd: defaultValues.get('HIFundingStructure.fundingTypeCd'),
      payrollFrequency: undefined,
      ...createEisTypeObject('HIFundingStructure', rootId, parentId),
    },
    offerStatus: 'offered',
    originalEffDate: coverageEffectiveDate,
    planStructure: defaultValues.get('HICoverageDefinition.planStructure'),
    ratingDetails: {
      rateBasisCd: defaultValues.get('HICoverageRating.rateBasisCd'),
      rateFormatCd: defaultValues.get('HICoverageRating.rateFormatCd'),
      ...createEisTypeObject('HICoverageRating', rootId, parentId),
    },
    terminationAgeCd: defaultValues.get('HICoverageDefinition.terminationAgeCd'),
    whoCovered: createWhoCovered(rootId, parentId),
    whoCoveredCd: defaultValues.get('HICoverageDefinition.whoCoveredCd'),
    issueAges: {
      minIssueAgeChild: defaultValues.get('HIEligibilityIssueAges.minIssueAgeChild'),
      maxIssueAgeChild: defaultValues.get('HIEligibilityIssueAges.maxIssueAgeChild'),
      minIssueAgeIndividual: defaultValues.get('HIEligibilityIssueAges.minIssueAgeIndividual'),
      minIssueAgeSpouse: defaultValues.get('HIEligibilityIssueAges.minIssueAgeSpouse'),
      ...createEisTypeObject('HIEligibilityIssueAges', rootId, parentId),
    },
    preExLimitationAndPregnancyWaitingPeriodDetails: {
      pregnancyWaitingPeriodMonthsCd: 'None',
      preExistingConditionLimitationPeriodCd: 'None',
      preExLimitationAndPregnancyWaitingPeriodCd: defaultValues.get(
        'HIPreExLimitationAndPregnancyWaitingPeriod.preExLimitationAndPregnancyWaitingPeriodCd'
      ),
      ...createEisTypeObject('HIPreExLimitationAndPregnancyWaitingPeriod', rootId, parentId),
    },
  };
};

const createHIEligibilities = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      waitingPeriodAmount: defaultValues.get('HIEligibility.waitingPeriodAmount'),
      waitingPeriodModeCd: defaultValues.get('HIEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('HIEligibility.minHourlyReq'),
      waitingPeriodDefCd: defaultValues.get('HIEligibility.waitingPeriodDefCd'),
      eligibilityTypeCd: 'ExistingEmployees',
    },
    {
      waitingPeriodModeCd: defaultValues.get('HIEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('HIEligibility.minHourlyReq'),
      eligibilityTypeCd: 'NewEmployees',
    },
  ].map(e => ({...e, ...createEisTypeObject('HIEligibility', rootId, parentId)}));
};

export const createGHI3BenefitStructure = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  covDefParentId: string
) => {
  const key = getKey(rootId, covDefParentId);
  const parentId = key.id;

  return {
    _key: key,
    _type: 'HIBenefitStructure',
    isHosAdmissionApplied: defaultValues.get('HIBenefitStructure.isHosAdmissionApplied'),
    hosAdmissionDetails: {
      benefitAmount: defaultValues.get('HIHosAdmissionBenefit.benefitAmount'),
      firstDayConfinementPaymentFreqLimitCd: defaultValues.get(
        'HIHosAdmissionBenefit.firstDayConfinementPaymentFreqLimitCd'
      ),
      ...createEisTypeObject('HIHosAdmissionBenefit', rootId, parentId),
    },

    //Routine - NewBorn Benefits
    isHospitalAdmissionRoutineNewbornApplied: defaultValues.get(
      'HIBenefitStructure.isHospitalAdmissionRoutineNewbornApplied'
    ),
    hosAdmissionRoutineNewbornDetails: {
      routineNewbornAmount: defaultValues.get('HIHosAdmissionRoutineNewBornBenefit.benefitAmount'),
      maxLimit: defaultValues.get('HIHosAdmissionRoutineNewBornBenefit.maxLimit'),
      ...createEisTypeObject('HIHosAdmissionRoutineNewBornBenefit', rootId, parentId),
    },
    isHospitalConfinementRoutineNewbornApplied: defaultValues.get(
      'HIBenefitStructure.isHospitalConfinementRoutineNewbornApplied'
    ),
    hosConfinementRoutineNewbornDetails: {
      routineNewbornAmount: defaultValues.get('HIConfinementRoutineNewBornBenefit.benefitAmount'),
      confinementLimit: defaultValues.get('HIConfinementRoutineNewBornBenefit.confinementLimit'),
      ...createEisTypeObject('HIConfinementRoutineNewBornBenefit', rootId, parentId),
    },

    isHosConfinementApplied: defaultValues.get('HIBenefitStructure.isHosConfinementApplied'),
    hosConfinementDetails: {
      benefitAmount: defaultValues.get('HIHosConfinementBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('HIHosConfinementBenefit.maxBenefitNumber'),
      ...createEisTypeObject('HIHosConfinementBenefit', rootId, parentId),
    },
    isICUConfinementApplied: defaultValues.get('HIBenefitStructure.isICUConfinementApplied'),
    icuConfinementDetails: {
      benefitAmount: defaultValues.get('HIICUConfinementBenefit.benefitAmount'),
      paymentFrequencyLimitCd: defaultValues.get('HIICUConfinementBenefit.paymentFrequencyLimitCd'),
      ...createEisTypeObject('HIICUConfinementBenefit', rootId, parentId),
    },
    portabilityDetails: {
      ...createEisTypeObject('HIPortability', rootId, parentId),
      limitPortedCovDurationInd: defaultValues.get('HIPortability.limitPortedCovDurationInd'),
      covDuration: defaultValues.get('HIPortability.covDuration'),
    },
    isStepDownIntensiveCareAdmissionApplied: defaultValues.get(
      'HIBenefitStructure.isStepDownIntensiveCareAdmissionApplied'
    ),
    icuStepDownAdmissionDetails: {
      benefitAmount: defaultValues.get('HIICUStepDownAdmissionBenefit.benefitAmount'),
      paymentFrequencyLimitCd: defaultValues.get('HIICUStepDownAdmissionBenefit.paymentFrequencyLimitCd'),
      ...createEisTypeObject('HIICUStepDownAdmissionBenefit', rootId, parentId),
    },
    isObservationUnitApplied: defaultValues.get('HIBenefitStructure.isObservationUnitApplied'),
    observationUnitDetails: defaultValues.get('HIBenefitStructure.isObservationUnitApplied')
      ? createHIObservationUnitDetails(defaultValues, rootId, parentId)
      : undefined,

    isDailyIntensiveCareConfinementApplied: true,
    icuDailyConfinementDetails: {
      benefitAmount: defaultValues.get('HIICUDailyConfinementBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('HIICUDailyConfinementBenefit.maxBenefitNumber'),
      ...createEisTypeObject('HIICUDailyConfinementBenefit', rootId, parentId),
    },

    isDailyStepDownIntensiveCareConfinmentApplied: true,
    icuDailyStepDownConfinementDetails: {
      benefitAmount: defaultValues.get('HIICUDailyStepDownConfinementBenefit.benefitAmount'),
      maxBenefitNumber: defaultValues.get('HIICUDailyStepDownConfinementBenefit.maxBenefitNumber'),
      ...createEisTypeObject('HIICUDailyStepDownConfinementBenefit', rootId, parentId),
    },
  };
};

const createBenefitPlanExclusions = (defaultValues: ImmutableMap<string, any>, rootId?: string, parentId?: string) =>
  createExclusions(
    [
      {
        exclusionEntityName: 'HIPlanExclMentalOrNervous',
        isExclusion: defaultValues.get('HIPlanExclMentalOrNervous.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclAlcoholism',
        isExclusion: defaultValues.get('HIPlanExclAlcoholism.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclParticipationDifferentAct',
        isExclusion: defaultValues.get('HIPlanExclParticipationDifferentAct.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclParticipationInAeronautics',
        isExclusion: defaultValues.get('HIPlanExclParticipationInAeronautics.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclVoluntaryTakingOfAnyPoison',
        isExclusion: defaultValues.get('HIPlanExclVoluntaryTakingOfAnyPoison.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclDrivingMotorizedVehicle',
        isExclusion: defaultValues.get('HIPlanExclDrivingMotorizedVehicle.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclOperatingVehicleWithBloodAlcohol',
        isExclusion: defaultValues.get('HIPlanExclOperatingVehicleWithBloodAlcohol.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclWarOrActOfWar',
        isExclusion: defaultValues.get('HIPlanExclWarOrActOfWar.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclFelonyOrAssaultOrIllegalOccupation',
        isExclusion: defaultValues.get('HIPlanExclFelonyOrAssaultOrIllegalOccupation.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclSuicide',
        isExclusion: defaultValues.get('HIPlanExclSuicide.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclCosmeticDentalOrPlasticSurgery',
        isExclusion: defaultValues.get('HIPlanExclCosmeticDentalOrPlasticSurgery.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclIntentionallySelfInflictedInjuries',
        isExclusion: defaultValues.get('HIPlanExclIntentionallySelfInflictedInjuries.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclConfinementBeforeEffDateOfCov',
        isExclusion: defaultValues.get('HIPlanExclConfinementBeforeEffDateOfCov.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclArtificialInsemination',
        isExclusion: defaultValues.get('HIPlanExclArtificialInsemination.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclReversalOfATubalLigationOrVasectomy',
        isExclusion: defaultValues.get('HIPlanExclReversalOfATubalLigationOrVasectomy.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclVoluntaryTakingOfAnyDrugOrSedative',
        isExclusion: defaultValues.get('HIPlanExclVoluntaryTakingOfAnyDrugOrSedative.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclTreatmentOutsideOfUnitedStates',
        isExclusion: defaultValues.get('HIPlanExclTreatmentOutsideOfUnitedStates.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclInjOrSickPriorToCoverageEffectiveDate',
        isExclusion: defaultValues.get('HIPlanExclInjOrSickPriorToCoverageEffectiveDate.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclWillfulCriminalActivity',
        isExclusion: defaultValues.get('HIPlanExclWillfulCriminalActivity.isExclusion'),
      },
      // the following two will be removed by offer API
      {
        exclusionEntityName: 'HIPlanExclNewbornCare',
        isExclusion: defaultValues.get('HIPlanExclNewbornCare.isExclusion'),
      },
      {
        exclusionEntityName: 'HIPlanExclIntoxicantNarcoticHallucinogenAlcohol',
        isExclusion: defaultValues.get('HIPlanExclIntoxicantNarcoticHallucinogenAlcohol.isExclusion'),
      },
    ],
    rootId,
    parentId
  );

const createExclusions = (
  exclusions: {exclusionEntityName: string; isExclusion: boolean}[],
  rootId?: string,
  parentId?: string
) =>
  exclusions.map(({exclusionEntityName, isExclusion}) => ({
    ...createEisTypeObject(exclusionEntityName, rootId, parentId),
    isExclusion: isExclusion,
  }));

const createWhoCovered = (rootId: string, parentId: string) =>
  ['Individual', 'IndividualAndSpouse', 'IndividualAndChildren', 'Family'].map(tierCd => ({
    ...createEisTypeObject('HITier', rootId, parentId),
    tierCd: tierCd,
  }));

const createHIObservationUnitDetails = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return {
    benefitAmount: defaultValues.get('HIObservationUnit.benefitAmount'),
    mindurationCd: defaultValues.get('HIObservationUnit.mindurationCd'),
    separationPeriodCd: defaultValues.get('HIObservationUnit.separationPeriodCd'),
    benefitStructure: defaultValues.get('HIObservationUnit.benefitStructure') ?? 'Basic',
    maxdurationCd: defaultValues.get('HIObservationUnit.maxdurationCd'),
    paymentFrequencyLimitCd: defaultValues.get('HIObservationUnit.paymentFrequencyLimitCd'),
    isPrePostOperativeCareApplied: defaultValues.get('HIObservationUnit.isPrePostOperativeCareApplied'),
    ...createEisTypeObject('HIObservationUnit', rootId, parentId),
  };
};
